import { Result, Skeleton } from 'antd';
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequirePermission,
  RequirePharmacyScope,
} from '@/auth/guards';
import type { Permission } from '@/auth/permissions';
import { AppLayout } from '@/layout/AppLayout';

/*
 * Pages load on demand. Antd is a large dependency and an admin panel visits
 * few of these screens in a session — the sign-in page in particular should not
 * carry the batch receipt form or the ledger with it.
 */
const load = <T extends Record<string, ComponentType>>(
  loader: () => Promise<T>,
  name: keyof T,
) => lazy(() => loader().then((module) => ({ default: module[name] })));

const LoginPage = load(() => import('@/features/auth/LoginPage'), 'LoginPage');
const DashboardPage = load(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage');

const MedicinesPage = load(() => import('@/features/catalog/MedicinesPage'), 'MedicinesPage');
const MedicineDetailPage = load(
  () => import('@/features/catalog/MedicineDetailPage'),
  'MedicineDetailPage',
);
const MedicineImportPage = load(
  () => import('@/features/catalog/MedicineImportPage'),
  'MedicineImportPage',
);
const BrandsPage = load(() => import('@/features/catalog/BrandsPage'), 'BrandsPage');
const RecallsPage = load(() => import('@/features/catalog/RecallsPage'), 'RecallsPage');
const CategoriesPage = load(() => import('@/features/catalog/CategoriesPage'), 'CategoriesPage');
const ManufacturersPage = load(
  () => import('@/features/catalog/ManufacturersPage'),
  'ManufacturersPage',
);


const StockHealthPage = load(
  () => import('@/features/inventory/StockHealthPage'),
  'StockHealthPage',
);

const ListingsPage = load(() => import('@/features/listings/ListingsPage'), 'ListingsPage');
const ListMedicinesPage = load(
  () => import('@/features/listings/ListMedicinesPage'),
  'ListMedicinesPage',
);
const SupplyRulesPage = load(
  () => import('@/features/listings/SupplyRulesPage'),
  'SupplyRulesPage',
);
const RetailRulesPage = load(
  () => import('@/features/listings/RetailRulesPage'),
  'RetailRulesPage',
);

const PharmaciesPage = load(
  () => import('@/features/pharmacies/PharmaciesPage'),
  'PharmaciesPage',
);

const OrdersPage = load(() => import('@/features/orders/OrdersPage'), 'OrdersPage');
const PrescriptionsPage = load(
  () => import('@/features/prescriptions/PrescriptionsPage'),
  'PrescriptionsPage',
);
const DeliveriesPage = load(
  () => import('@/features/deliveries/DeliveriesPage'),
  'DeliveriesPage',
);
const RidersPage = load(() => import('@/features/deliveries/RidersPage'), 'RidersPage');
const SettlementsPage = load(
  () => import('@/features/settlements/SettlementsPage'),
  'SettlementsPage',
);
const UsersPage = load(() => import('@/features/users/UsersPage'), 'UsersPage');
const AuditPage = load(() => import('@/features/audit/AuditPage'), 'AuditPage');
const FeesPage = load(() => import('@/features/settings/FeesPage'), 'FeesPage');
const MessagingPage = load(
  () => import('@/features/settings/MessagingPage'),
  'MessagingPage',
);
const SecurityPage = load(() => import('@/features/settings/SecurityPage'), 'SecurityPage');
const RegionalPage = load(() => import('@/features/settings/RegionalPage'), 'RegionalPage');
const CouponsPage = load(() => import('@/features/coupons/CouponsPage'), 'CouponsPage');

/** Wraps a page in its guards and a suspense fallback, so the route table stays readable. */
function guarded(
  element: ReactNode,
  options: { permission?: Permission; pharmacyScoped?: boolean } = {},
): ReactNode {
  let node: ReactNode = <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>{element}</Suspense>;
  if (options.pharmacyScoped) node = <RequirePharmacyScope>{node}</RequirePharmacyScope>;
  if (options.permission) {
    node = <RequirePermission permission={options.permission}>{node}</RequirePermission>;
  }
  return node;
}

const protectedRoutes: RouteObject[] = [
  { index: true, element: guarded(<DashboardPage />) },

  {
    path: 'catalog/medicines',
    element: guarded(<MedicinesPage />, { permission: 'catalog:read' }),
  },
  {
    path: 'catalog/medicines/import',
    element: guarded(<MedicineImportPage />, { permission: 'catalog:write' }),
  },
  {
    path: 'catalog/medicines/:medicineId',
    element: guarded(<MedicineDetailPage />, { permission: 'catalog:read' }),
  },
  {
    path: 'catalog/brands',
    element: guarded(<BrandsPage />, { permission: 'catalog:read' }),
  },
  {
    path: 'catalog/recalls',
    element: guarded(<RecallsPage />, { permission: 'catalog:read' }),
  },
  {
    path: 'catalog/categories',
    element: guarded(<CategoriesPage />, { permission: 'catalog:read' }),
  },
  {
    path: 'catalog/manufacturers',
    element: guarded(<ManufacturersPage />, { permission: 'catalog:read' }),
  },


  {
    path: 'inventory',
    element: guarded(<StockHealthPage />, { permission: 'inventory:read:any' }),
  },
  {
    path: 'listings',
    element: guarded(<ListingsPage />, { permission: 'listing:read', pharmacyScoped: true }),
  },
  /* A page, not a drawer: the unit of work is a whole shelf at once. */
  {
    path: 'listings/new',
    element: guarded(<ListMedicinesPage />, { permission: 'listing:write', pharmacyScoped: true }),
  },
  {
    path: 'supply-rules',
    element: guarded(<SupplyRulesPage />, {
      permission: 'settlement:write',
      pharmacyScoped: true,
    }),
  },
  /*
   * Platform-wide, so deliberately NOT pharmacy-scoped: what we sell a pack for
   * is one decision taken once, not a per-shop negotiation.
   */
  {
    path: 'retail-rules',
    element: guarded(<RetailRulesPage />, { permission: 'settlement:read:any' }),
  },

  { path: 'orders', element: guarded(<OrdersPage />, { permission: 'order:read:any' }) },
  {
    path: 'prescriptions',
    element: guarded(<PrescriptionsPage />, { permission: 'prescription:read:any' }),
  },
  { path: 'deliveries', element: guarded(<DeliveriesPage />, { permission: 'delivery:read' }) },
  { path: 'riders', element: guarded(<RidersPage />, { permission: 'delivery:assign' }) },
  { path: 'settlements', element: guarded(<SettlementsPage />, { permission: 'settlement:read' }) },
  { path: 'pharmacies', element: guarded(<PharmaciesPage />, { permission: 'pharmacy:read' }) },
  { path: 'users', element: guarded(<UsersPage />, { permission: 'user:read' }) },
  /*
   * SUPER_ADMIN only. `audit:read` is held by no other role, so an ADMIN never
   * reaches this route — the sidebar does not offer it and a typed URL lands on
   * the same refusal every other unheld permission gets.
   */
  { path: 'audit', element: guarded(<AuditPage />, { permission: 'audit:read' }) },
  /*
   * Guarded on `config:read`, not `config:write`: reading the fare table is an
   * everyday operational need — it is what a delivery charge is explained from
   * — while changing it is SUPER_ADMIN's. The Save buttons require
   * `config:write`, and the server checks again.
   */
  { path: 'settings/fees', element: guarded(<FeesPage />, { permission: 'config:read' }) },
  /*
   * Guarded on `config:write`, unlike the fares above — and the READ is guarded
   * too, which is the unusual part.
   *
   * The server gates both of these on `config:write`, including GET, because
   * the responses describe which gateway carries every verification code and
   * exactly how many guesses the login endpoint tolerates. `config:write` is
   * held by SUPER_ADMIN alone, so matching it here means an ADMIN never reaches
   * a page that would answer 403 and look broken.
   */
  {
    path: 'settings/messaging',
    element: guarded(<MessagingPage />, { permission: 'config:write' }),
  },
  {
    path: 'settings/security',
    element: guarded(<SecurityPage />, { permission: 'config:write' }),
  },
  /*
   * Back to `config:read`, like the fares and unlike the two above.
   *
   * The platform timezone is the clock every date in this panel is rendered
   * on, so an operator who cannot see which zone that is cannot trust a single
   * timestamp they are looking at. The server gates `GET /admin/config` on
   * `config:read` for the same reason. Saving needs `config:write`, which the
   * page enforces on the control and the server enforces again.
   */
  { path: 'settings/regional', element: guarded(<RegionalPage />, { permission: 'config:read' }) },
  { path: 'coupons', element: guarded(<CouponsPage />, { permission: 'coupon:read' }) },

  {
    path: '*',
    element: <Result status="404" title="No such page" subTitle="That route does not exist." />,
  },
];

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <RedirectIfAuthenticated>
        <Suspense fallback={null}>
          <LoginPage />
        </Suspense>
      </RedirectIfAuthenticated>
    ),
  },
  {
    element: <RequireAuth />,
    children: [{ path: '/', element: <AppLayout />, children: protectedRoutes }],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
